import { FC, useEffect, useRef, useState } from 'react';

export interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const formatShort = (d: Date) =>
  d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const toInputValue = (d: Date | null) => (d ? d.toISOString().split('T')[0] : '');

export const DateRangeFilter: FC<DateRangeFilterProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(value);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setDraft(value);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  const buttonText =
    value.from && value.to
      ? `${formatShort(value.from)} - ${formatShort(value.to)}`
      : value.from
        ? `From ${formatShort(value.from)}`
        : value.to
          ? `Until ${formatShort(value.to)}`
          : 'Date Range';

  const handleApply = () => {
    onChange(draft);
    setIsOpen(false);
  };

  const handleClear = () => {
    const cleared = { from: null, to: null };
    setDraft(cleared);
    onChange(cleared);
    setIsOpen(false);
  };

  return (
    <div className="relative flex flex-col gap-1 md:col-span-2" ref={containerRef}>
      <label className="text-xs font-medium text-white-80">Date Range</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="bg-dark-green flex h-9 w-full items-center justify-between truncate rounded border border-black-20 px-3 py-1 text-left text-sm text-white focus:border-white-40 focus:outline-none"
      >
        <span className="truncate pr-2">{buttonText}</span>
        <span className="text-[10px] text-white-40">▼</span>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-[60px] z-50 w-full min-w-[280px] rounded border border-gray-200 bg-dark-green-950 p-3 text-white-80 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="flex flex-1 flex-col gap-1">
              <label htmlFor="date-range-from" className="text-xs font-medium text-white-80">
                From
              </label>
              <input
                id="date-range-from"
                type="date"
                value={toInputValue(draft.from)}
                max={toInputValue(draft.to) || undefined}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    from: e.target.value ? new Date(e.target.value) : null,
                  }))
                }
                className="h-9 w-full rounded border border-black-20 !bg-dark-green-950 px-2 text-sm text-white [color-scheme:dark] focus:!border-white-40 focus:outline-none"
              />
            </div>
            <span className="mt-5 text-white-40">-</span>
            <div className="flex flex-1 flex-col gap-1">
              <label htmlFor="date-range-to" className="text-xs font-medium text-white-80">
                To
              </label>
              <input
                id="date-range-to"
                type="date"
                value={toInputValue(draft.to)}
                min={toInputValue(draft.from) || undefined}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    to: e.target.value ? new Date(e.target.value) : null,
                  }))
                }
                className="h-9 w-full rounded border border-black-20 !bg-dark-green-950 px-2 text-sm text-white [color-scheme:dark] focus:!border-white-40 focus:outline-none"
              />
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 rounded bg-white px-3 py-1.5 text-sm font-semibold text-[hsl(var(--color-dark-green-900))] transition-opacity hover:opacity-90"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="flex-1 rounded border border-black-20 bg-transparent px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/5"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
